import React, { useCallback, useMemo, useState } from 'react'
import { Grid, Rating } from '@mui/material'
import { Typography } from '@material-ui/core'
import { Datagrid, Tooltip as ReferaTooltip } from '@refera/ui-web'
import Svg from '_components/svg'
import useStyles from './styles'
import moment from 'moment'
import { STAGE_TYPES_COMPANY_LABEL, REGISTER_STATUS_LABEL, REGISTER_STATUS } from '_utils/constants'
import { phoneNumberMask } from '_utils/helpers'
import Chip from '_components/chip'
import CategoryIndicator from '_components/category-indicator'
import Button from '_components/button'
import { WhatsApp } from '@material-ui/icons'
import { openWhatsAppWithMessage } from '_/utils/open-whatsApp'
import { RateProviderModal } from '_/views/manage-providers/components/RateProviderModal'
import useToggle from '_hooks/use-toggle'
import useRolePermission from '_/hooks/use-role-permission'
import IcCheck from '_/assets/icons/ic-check.svg'
import IcWarning from '_/assets/icons/ic-warning.svg'
import WarningBlueIcon from '_assets/icons/ic-warning-blue.svg'
import { LEVEL_OPTIONS } from '_/views/provider/components/ProviderReferaDetailsModal/constants'
import Tooltip from '_/components/tooltip'

const REGISTRATION_FIELD_LABELS = {
  name: 'Nome da empresa',
  mainServices: 'Categorias de manutenção',
  cities: 'Região de atendimento',
  cnpj: 'CPF/CNPJ',
}

function getMissingRegistrationFields(provider) {
  if (!provider || provider.registrationStatus === REGISTER_STATUS.COMPLETE) return []
  const missing = []
  if (!provider.name || String(provider.name || '').trim() === '') {
    missing.push(REGISTRATION_FIELD_LABELS.name)
  }
  const hasMainServices =
    (Array.isArray(provider.mainServices) && provider.mainServices.length > 0) ||
    (Array.isArray(provider.mainServiceNames) && provider.mainServiceNames.length > 0) ||
    (provider.mainServiceNames != null && String(provider.mainServiceNames).trim() !== '')
  if (!hasMainServices) {
    missing.push(REGISTRATION_FIELD_LABELS.mainServices)
  }
  const hasCities = Array.isArray(provider.cities) && provider.cities.length > 0
  if (!hasCities) {
    missing.push(REGISTRATION_FIELD_LABELS.cities)
  }
  if (provider.cnpj == null || String(provider.cnpj || '').trim() === '') {
    missing.push(REGISTRATION_FIELD_LABELS.cnpj)
  }
  return missing
}

/** Usada apenas quando o back retorna cadastro incompleto, para explicar os dados faltantes. */
function formatIncompleteTooltip(missing) {
  if (missing.length === 0) return 'Cadastro incompleto.'
  const list = missing.map(item => `• ${item}`).join('\n')
  return `Cadastro incompleto.\n\nDados faltantes:\n${list}`
}

const TABLE_SX = {
  fontSize: '1.4rem',
  width: '100%',
  '& .MuiDataGrid-cell:hover': {
    cursor: 'pointer',
  },
}

const dateFormats = ['DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm', 'DD/MM/YYYY']

const WhatsappButton = ({ phone }) => {
  const styles = useStyles()
  return (
    <Button
      variant="default"
      title="Enviar mensagem de WhatsApp"
      onClick={e => {
        e.stopPropagation()
        openWhatsAppWithMessage({ phone })
      }}
    >
      <WhatsApp className={styles.iconWhatsApp} />
    </Button>
  )
}

const RatingComponent = ({ params, onClick }) => {
  return (
    <Button
      onClick={e => {
        e.stopPropagation()
        onClick(params.row)
      }}
    >
      <Rating value={params.value ?? 0} precision={0.5} readOnly size="large" />
    </Button>
  )
}

export default function DataTable({
  dataToShow,
  count,
  onRowClick,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onSortModelChange,
  loading,
}) {
  const styles = useStyles()
  const [openRateModal, handleOpenModal] = useToggle()
  const [isModalViewMode, setIsModalViewMode] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState(null)
  const [isGeneralRate, setIsGeneralRate] = useState(false)
  const { isSAAS } = useRolePermission()

  const handleOpenRatingModal = useCallback(
    ({ viewMode, isGeneral, providerId }) => {
      setSelectedProvider(providerId)
      setIsModalViewMode(viewMode)
      setIsGeneralRate(isGeneral)
      handleOpenModal()
    },
    [handleOpenModal]
  )

  const getLevelLabel = useCallback(level => {
    return LEVEL_OPTIONS.find(itemLevel => itemLevel?.value === level)?.label || ''
  })

  const ProviderEvaluationContent = ({ item }) => {
    const { level, servicesCount, warrantyPercentage, budgetConversion } = item?.value || {}

    const hasData = Object.values({
      level,
      servicesCount,
      warrantyPercentage,
      budgetConversion,
    }).some(v => v !== null && v !== undefined && v !== '' && v !== 0)

    if (!hasData) {
      return (
        <Typography variant="body2" style={{ textAlign: 'center', padding: '4px 8px' }}>
          Prestador não realizou serviços o suficiente com a Refera para gerar classificação.
        </Typography>
      )
    }

    return (
      <>
        <Typography variant="body2">
          <strong>Classificação:</strong> {getLevelLabel(level) || '-'}
        </Typography>
        <Typography variant="body2">
          <strong>Qtd. de serviços realizados:</strong> {servicesCount ?? '-'}
        </Typography>
        <Typography variant="body2">
          <strong>% de garantias:</strong> {warrantyPercentage ?? '-'}%
        </Typography>
        <Typography variant="body2">
          <strong>Conv. de orçamentos:</strong> {budgetConversion ?? '-'}%
        </Typography>
      </>
    )
  }

  const columns = useMemo(
    () => [
      {
        field: 'nome',
        headerName: 'Nome',
        width: 400,
        sortable: true,
        renderCell: params => {
          const registrationDate = moment(params.row.data_cadastro, dateFormats)
          const isNew = moment().diff(registrationDate, 'days') <= 30

          return (
            <Grid container alignItems="center">
              {params.value}
              {isNew && <Chip label="Novo" className={styles.newChip} />}
            </Grid>
          )
        },
      },
      ...(!isSAAS
        ? [
            {
              field: 'responsavel',
              headerName: 'Responsavel',
              flex: 1,
              width: 150,
              sortable: true,
            },
          ]
        : []),
      {
        field: 'whatsapp',
        headerName: 'Whatsapp',
        width: 80,
        renderCell: params =>
          params.row.fullPhone ? <WhatsappButton phone={params.row.fullPhone} /> : '',
        sortable: false,
      },

      {
        field: 'categoria',
        headerName: 'Categoria',
        flex: 1,
        minWidth: 120,
        renderCell: params => {
          const categories =
            params.value
              ?.split(',')
              .map(c => c.trim())
              .filter(c => c) || []
          if (categories.length === 0) return null
          if (categories.length > 1) {
            return (
              <Grid container alignItems="center">
                <Chip label={categories[0]} className={styles.category} />
                <CategoryIndicator
                  tooltipText={categories.join(', ').replace(/, ((?:.(?!, ))+)$/, ' e $1')}
                  label={`+${categories.length - 1}`}
                />
              </Grid>
            )
          }
          return <Chip label={categories[0]} className={styles.category} />
        },
        sortable: true,
      },
      { field: 'cidade', headerName: 'Cidade', flex: 1, minWidth: 120, sortable: true },
      ...(isSAAS
        ? [
            {
              field: 'agencyRate',
              headerName: 'Minha avaliação',
              flex: 1,
              minWidth: 100,
              renderCell: params => {
                return (
                  <RatingComponent
                    params={params}
                    onClick={row =>
                      handleOpenRatingModal({
                        viewMode: false,
                        isGeneral: false,
                        providerId: row?.id,
                      })
                    }
                  />
                )
              },
              sortable: true,
            },
            {
              field: 'generalRate',
              headerName: 'Avaliação de imobiliárias',
              flex: 1,
              minWidth: 100,
              renderCell: params => {
                return (
                  <RatingComponent
                    params={params}
                    onClick={row =>
                      handleOpenRatingModal({
                        viewMode: true,
                        isGeneral: true,
                        providerId: row?.id,
                      })
                    }
                  />
                )
              },
              sortable: true,
            },
            {
              field: 'referaRate',
              headerName: 'Avaliação da Refera',
              flex: 1,
              minWidth: 100,
              renderCell: params => {
                return (
                  <Grid>
                    <Tooltip
                      triggerType="click"
                      title={<ProviderEvaluationContent item={params?.row} />}
                      tooltipClass={styles.tooltip}
                    >
                      <Typography className={styles.tooltipTrigger}>
                        <Svg icon={WarningBlueIcon} className={styles.supportIcon} />
                        <span className={styles.link}>Avaliação</span>
                      </Typography>
                    </Tooltip>
                  </Grid>
                )
              },
              sortable: true,
            },
          ]
        : []),
      {
        field: 'etapaLabel',
        headerName: 'Etapa',
        flex: 1,
        width: 50,
        renderCell: params => {
          if (!params.value || params.value.trim() === '') return null
          return <Chip label={params.value} />
        },
        sortable: true,
      },
      {
        field: 'statusLabel',
        headerName: 'Status',
        width: 120,
        renderCell: params => {
          const isComplete = params.row.status === REGISTER_STATUS.COMPLETE
          const tooltipTitle = isComplete
            ? 'Cadastro completo'
            : formatIncompleteTooltip(params.row.missingRegistrationFields || [])
          return (
            <ReferaTooltip
              title={
                <div style={{ fontSize: '1.6rem', whiteSpace: 'pre-line', lineHeight: 1.0 }}>
                  {tooltipTitle}
                </div>
              }
            >
              <Grid container alignItems="center">
                {isComplete ? (
                  <Svg icon={IcCheck} alt="check" style={{ color: 'green' }} />
                ) : (
                  <Svg icon={IcWarning} alt="warning" style={{ color: 'orange' }} />
                )}
              </Grid>
            </ReferaTooltip>
          )
        },
        sortable: true,
      },
    ],
    [styles.category, styles.chip, styles.incomplete]
  )

  const orderByDefault = useMemo(() => {
    const firstColumn = columns[0]
    if (!firstColumn) return null
    return firstColumn.field
  }, [columns])

  const rows = useMemo(() => {
    if (!dataToShow) return []

    return dataToShow.map(provider => ({
      id: provider.id,
      nome: provider.name,
      responsavel: provider.personResponsibleName,
      whatsapp: phoneNumberMask(provider.phone),
      fullPhone: provider.phone,
      categoria: provider.mainServiceNames,
      cidade: provider.city,
      data_cadastro: provider.providerSignupDate
        ? moment(provider.providerSignupDate).format('DD/MM/YYYY')
        : '',
      qtd_servicos: provider.servicesCount,
      cat_performance: provider.level,
      level: provider.level,
      servicesCount: provider.servicesCount,
      warrantyPercentage: provider.warrantyPercentage,
      budgetConversion: provider.budgetConversion,
      etapa: provider.onboardingStage,
      etapaLabel: STAGE_TYPES_COMPANY_LABEL[provider.onboardingStage] || provider.onboardingStage,
      status: provider.registrationStatus,
      statusLabel:
        REGISTER_STATUS_LABEL[provider.registrationStatus] || provider.registrationStatus,
      missingRegistrationFields:
        provider.registrationStatus === REGISTER_STATUS.COMPLETE
          ? []
          : getMissingRegistrationFields(provider),
      agencyRate: provider?.agencyAverageRate,
      generalRate: provider?.generalAverageRate,
    }))
  }, [dataToShow, page, pageSize])

  const handleCellClick = useCallback(({ row }) => {
    if (row?.link) {
      window.open(`${row?.link}`, '_blank', 'noopener,noreferrer')
    }
  }, [])
  return (
    <Grid className={styles.mainTable}>
      <Datagrid
        initialState={{
          sorting: {
            sortModel: [{ field: orderByDefault, sort: 'asc' }],
          },
        }}
        loading={!!loading}
        rows={rows}
        columns={columns}
        onCellClick={handleCellClick}
        onRowClick={onRowClick}
        rowCount={count || 0}
        sx={TABLE_SX}
        noRowsLabel="Nenhum resultado encontrado"
        paginationMode="server"
        onPageChange={onPageChange}
        page={page}
        onPageSizeChange={onPageSizeChange}
        pageSize={pageSize}
        onSortModelChange={onSortModelChange}
      />
      <RateProviderModal
        open={openRateModal}
        setOpen={handleOpenModal}
        viewMode={isModalViewMode}
        data={{ providerId: selectedProvider, isGeneralRate }}
      />
    </Grid>
  )
}
